import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { isPracticalType } from '../../utils/practicalResults';
import { ACCENT_PROJECT, ACCENT_ACADEMIC } from '../../lib/personAccents';

interface OutcomeItem {
  type: string;
  year: number;
  is_practical?: boolean;
}

interface ProjectItem {
  year: number;
}

interface OutcomesByYearChartProps {
  outcomes: OutcomeItem[];
  projects: ProjectItem[];
}

interface YearBucket {
  year: number;
  practical: number;
  academic: number;
  project: number;
}

const chartConfig = {
  practical: {
    label: 'Practical',
    color: 'hsl(var(--primary))',
  },
  academic: {
    label: 'Other',
    color: ACCENT_ACADEMIC,
  },
  project: {
    label: 'Project',
    color: ACCENT_PROJECT,
  },
} satisfies ChartConfig;

export default function OutcomesByYearChart({ outcomes, projects }: OutcomesByYearChartProps) {
  const { t } = useTranslation('person');
  const currentYear = new Date().getFullYear();

  const data = useMemo<YearBucket[]>(() => {
    if (outcomes.length === 0 && projects.length === 0) return [];

    const validOutcomeYears = outcomes
      .map(o => o.year)
      .filter((y): y is number => typeof y === 'number' && Number.isFinite(y));
    const validProjectYears = projects
      .map(p => p.year)
      .filter((y): y is number => typeof y === 'number' && Number.isFinite(y));
    const allYears = [...validOutcomeYears, ...validProjectYears];
    if (allYears.length === 0) return [];

    const minYear = Math.min(...allYears);
    const maxYear = Math.max(...allYears, currentYear);

    const buckets = new Map<number, YearBucket>();
    for (let y = minYear; y <= maxYear; y++) {
      buckets.set(y, { year: y, practical: 0, academic: 0, project: 0 });
    }
    for (const o of outcomes) {
      const bucket = buckets.get(o.year);
      if (!bucket) continue;
      const practical = o.is_practical || isPracticalType(o.type);
      if (practical) bucket.practical += 1;
      else bucket.academic += 1;
    }
    for (const p of projects) {
      const bucket = buckets.get(p.year);
      if (!bucket) continue;
      bucket.project += 1;
    }
    return Array.from(buckets.values());
  }, [outcomes, projects, currentYear]);

  const showCurrentYearLine =
    data.length > 0 &&
    data[0].year <= currentYear &&
    data[data.length - 1].year >= currentYear;

  const span = data.length;
  const sparseTicks = span > 14;

  const localizedConfig: ChartConfig = {
    practical: { ...chartConfig.practical, label: t('resultsSection.practical') },
    academic: { ...chartConfig.academic, label: t('resultsSection.academic') },
    project: { ...chartConfig.project, label: t('sections.projects') },
  };

  return (
    <Card className="border-ctu-border shadow-none flex flex-col h-full">
      <CardContent className="flex flex-col gap-3 px-6 pt-5 pb-4 flex-1 min-h-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {t('sections.outcomesByYear')}
          </span>
          <span className="text-xs text-ctu-text-muted">
            {t('sections.outcomesByYearSubtitle')}
          </span>
        </div>

        {data.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-10 text-sm text-ctu-text-muted">
            {t('resultsSection.noResults')}
          </div>
        ) : (
          <ChartContainer config={localizedConfig} className="aspect-auto w-full flex-1 min-h-0">
            <BarChart
              data={data}
              margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
              barCategoryGap={2}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={sparseTicks ? 'preserveStartEnd' : 0}
                minTickGap={sparseTicks ? 24 : 0}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={36}
                allowDecimals={false}
                tickMargin={4}
              />
              <ChartTooltip
                cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    labelFormatter={(value) => String(value)}
                  />
                }
              />
              <ChartLegend
                content={<ChartLegendContent />}
                verticalAlign="bottom"
                height={28}
              />
              <Bar
                dataKey="practical"
                stackId="outcomes"
                fill="var(--color-practical)"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="academic"
                stackId="outcomes"
                fill="var(--color-academic)"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="project"
                stackId="outcomes"
                fill="var(--color-project)"
                radius={[2, 2, 0, 0]}
              />
              {showCurrentYearLine && (
                <ReferenceLine
                  x={currentYear}
                  stroke="hsl(var(--foreground))"
                  strokeDasharray="4 4"
                  strokeOpacity={0.45}
                  label={{
                    value: t('chart.currentYear'),
                    position: 'top',
                    fill: 'hsl(var(--foreground))',
                    fillOpacity: 0.6,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                />
              )}
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
